<app-notification></app-notification>

<div class="min-h-screen bg-gray-900 py-8">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
      <div>
        <h1 class="text-3xl font-bold text-white">WordPress Import</h1>
        <p class="mt-1 text-sm text-gray-400" *ngIf="connected">
          {{ totalPosts }} posts gevonden op {{ siteName }}
        </p>
        <p class="mt-1 text-sm text-gray-400" *ngIf="!connected">
          Voer een WordPress URL in om posts te importeren
        </p>
      </div>
      <div class="mt-4 sm:mt-0">
        <button
          (click)="goBack()"
          class="inline-flex items-center px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors">
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Terug naar berichten
        </button>
      </div>
    </div>

    <!-- URL Input -->
    <div class="bg-gray-800 rounded-xl border border-gray-700 p-5 mb-6">
      <label class="block text-sm font-medium text-gray-300 mb-2">WordPress site URL</label>
      <div class="flex gap-3">
        <input
          type="url"
          [(ngModel)]="wpUrl"
          placeholder="https://example.com of https://example.com/wp-json"
          (keydown.enter)="connectToSite()"
          class="flex-1 bg-gray-700 border border-gray-600 text-white text-sm rounded-lg px-4 py-2.5 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500" />
        <button
          (click)="connectToSite()"
          [disabled]="connecting"
          class="inline-flex items-center px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap">
          <svg *ngIf="connecting" class="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          <svg *ngIf="!connecting" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          {{ connecting ? 'Verbinden...' : 'Ophalen' }}
        </button>
      </div>
      <p *ngIf="connected" class="mt-2 text-xs text-green-400 flex items-center gap-1">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
        Verbonden met {{ siteName }} · {{ wpCategories.length }} categorieën
      </p>
    </div>

    <!-- Loading -->
    <div *ngIf="loading" class="flex justify-center items-center py-20">
      <div class="flex items-center gap-3 text-gray-400">
        <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        WordPress posts laden...
      </div>
    </div>

    <!-- Posts List -->
    <div *ngIf="!loading && connected" class="space-y-4">
      <div *ngFor="let item of posts"
           class="bg-gray-800 rounded-xl border transition-all"
           [ngClass]="item.imported ? 'border-green-700/50 opacity-60' : 'border-gray-700'">

        <!-- Post Card -->
        <div class="p-5">
          <div class="flex items-start gap-4">
            <!-- Featured Image -->
            <div *ngIf="getFeaturedImageUrl(item.wp)" class="flex-shrink-0 hidden sm:block">
              <img [src]="getFeaturedImageUrl(item.wp)" [alt]="stripHtml(item.wp.title.rendered)"
                   class="w-20 h-20 object-cover rounded-lg border border-gray-700" />
            </div>

            <!-- Post Info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <h3 class="text-base font-semibold text-white leading-tight truncate">
                    {{ stripHtml(item.wp.title.rendered) }}
                  </h3>
                  <div class="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                    <span class="text-xs text-gray-500">{{ item.wp.date | date:'d MMM yyyy' }}</span>
                    <span class="text-xs text-gray-600">·</span>
                    <span class="text-xs text-gray-500 font-mono">{{ item.wp.slug }}</span>
                    <span *ngIf="item.wp.categories.length" class="text-xs text-gray-600">·</span>
                    <span *ngIf="item.wp.categories.length" class="text-xs text-indigo-400">
                      {{ getWpCategoryNames(item.wp.categories) }}
                    </span>
                  </div>
                </div>

                <!-- Status badge -->
                <div *ngIf="item.imported"
                     class="flex-shrink-0 inline-flex items-center px-2.5 py-1 bg-green-900/40 text-green-400 text-xs font-medium rounded-full border border-green-800/50">
                  <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Geïmporteerd
                </div>
              </div>

              <!-- Excerpt -->
              <p class="text-sm text-gray-400 mt-2 line-clamp-2">
                {{ stripHtml(item.wp.excerpt.rendered) }}
              </p>

              <!-- Actions -->
              <div class="flex items-center gap-2 mt-3" *ngIf="!item.imported && !item.showConfig">
                <button
                  (click)="showImportConfig(item)"
                  class="inline-flex items-center px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded-lg transition-colors">
                  <svg class="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Importeer
                </button>
                <button
                  (click)="toggleExpand(item)"
                  class="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                  [ngClass]="item.expanded ? 'bg-indigo-900/30 text-indigo-300 border border-indigo-700/50' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'">
                  <svg class="w-3.5 h-3.5 mr-1.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                       [style.transform]="item.expanded ? 'rotate(180deg)' : 'rotate(0)'">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                  {{ item.expanded ? 'Verberg content' : 'Bekijk content' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Expanded Content Preview -->
          <div *ngIf="item.expanded" class="mt-4 rounded-lg border border-gray-700 overflow-hidden">
            <div class="px-4 py-2 bg-gray-750 border-b border-gray-700 flex items-center justify-between"
                 style="background: rgba(55,65,81,0.5);">
              <span class="text-xs font-medium text-gray-400">Content preview</span>
              <button (click)="toggleExpand(item)" class="text-gray-500 hover:text-gray-300">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="p-5 bg-gray-900/50 max-h-[500px] overflow-y-auto wp-content" [innerHTML]="item.wp.content.rendered"></div>
          </div>
        </div>

        <!-- Import Config Panel -->
        <div *ngIf="item.showConfig && !item.imported"
             class="border-t border-gray-700 p-5 rounded-b-xl"
             style="background: rgba(17,24,39,0.5);">
          <h4 class="text-sm font-medium text-gray-300 mb-3">Import instellingen</h4>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <!-- Domain -->
            <div>
              <label class="block text-xs text-gray-400 mb-1">Domein</label>
              <select [(ngModel)]="item.selectedDomain"
                      class="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-lg px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option *ngFor="let d of domains" [value]="d.name">{{ d.name }}</option>
              </select>
            </div>

            <!-- Language -->
            <div>
              <label class="block text-xs text-gray-400 mb-1">Taal</label>
              <select [(ngModel)]="item.selectedLanguage"
                      class="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-lg px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="nl">Nederlands</option>
                <option value="en">English</option>
                <option value="de">Deutsch</option>
                <option value="fr">Français</option>
              </select>
            </div>

            <!-- Status -->
            <div>
              <label class="block text-xs text-gray-400 mb-1">Status</label>
              <select [(ngModel)]="item.selectedStatus"
                      class="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-lg px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500">
                <option value="draft">Concept</option>
                <option value="published">Gepubliceerd</option>
              </select>
            </div>
          </div>

          <!-- Categories -->
          <div class="mt-4" *ngIf="categories.length > 0">
            <label class="block text-xs text-gray-400 mb-2">Categorieën</label>
            <div class="flex flex-wrap gap-2">
              <button *ngFor="let cat of categories"
                      (click)="toggleCategory(item, cat.id)"
                      class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full transition-colors"
                      [ngClass]="item.selectedCategoryIds.includes(cat.id)
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'">
                {{ cat.name }}
              </button>
            </div>
          </div>

          <!-- Yoast SEO preview -->
          <div *ngIf="item.wp.yoast_head_json?.description" class="mt-4 p-3 bg-gray-800 rounded-lg border border-gray-700">
            <p class="text-xs text-gray-500 mb-1">Yoast SEO data (wordt meegenomen)</p>
            <p class="text-xs text-gray-400">{{ item.wp.yoast_head_json?.description }}</p>
          </div>

          <!-- Confirm / Cancel -->
          <div class="flex items-center gap-2 mt-4">
            <button
              (click)="confirmImport(item)"
              [disabled]="item.importing"
              class="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
              <svg *ngIf="item.importing" class="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              {{ item.importing ? 'Importeren...' : 'Bevestig import' }}
            </button>
            <button
              (click)="cancelImport(item)"
              [disabled]="item.importing"
              class="inline-flex items-center px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm font-medium rounded-lg transition-colors">
              Annuleer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div *ngIf="!loading && connected && totalPages > 1" class="mt-6 flex items-center justify-between">
      <button
        (click)="prevPage()"
        [disabled]="currentPage <= 1"
        class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
        <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        Vorige
      </button>
      <span class="text-sm text-gray-400">
        Pagina {{ currentPage }} van {{ totalPages }}
      </span>
      <button
        (click)="nextPage()"
        [disabled]="currentPage >= totalPages"
        class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
        Volgende
        <svg class="w-4 h-4 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>

    <!-- Empty State (connected but no posts) -->
    <div *ngIf="!loading && connected && posts.length === 0" class="text-center py-20">
      <svg class="mx-auto h-12 w-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <p class="mt-3 text-gray-400">Geen publieke WordPress posts gevonden</p>
      <p class="mt-1 text-sm text-gray-500">Controleer of er gepubliceerde posts zijn op deze site</p>
    </div>

    <!-- Not connected state -->
    <div *ngIf="!loading && !connected && !connecting" class="text-center py-20">
      <svg class="mx-auto h-16 w-16 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
      </svg>
      <p class="mt-4 text-gray-400">Voer hierboven een WordPress URL in en klik op <strong class="text-white">Ophalen</strong></p>
      <p class="mt-1 text-sm text-gray-500">Elke WordPress site met de REST API ingeschakeld wordt ondersteund</p>
    </div>
  </div>
</div>
